import React, {
  Fragment,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  useFormContext,
} from 'react-hook-form';
import FormController from 'core/components/FormController';
import MaskedTextField from 'core/components/MaskedTextField';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  VitalEditorFieldConfig,
  VitalFieldKey,
  VitalsFormValues,
} from 'pages/Dashboard/pages/Encounters/pages/List/components/PatientDetails/Vitals/types';

type Props = {
  config: VitalEditorFieldConfig;
  isEditing?: boolean;
  values?: Partial<Record<VitalFieldKey, string>>;
};

const inputWidthByKey: Record<string, string> = {
  heartRateBpm: '!min-w-[2rem] !max-w-[2.25rem]',
  respiratoryRateBreathsPerMinute: '!min-w-[1.75rem] !max-w-[2rem]',
  temperatureFahrenheit: '!min-w-[2.25rem] !max-w-[2.5rem]',
  oxygenSaturationPercent: '!min-w-[2rem] !max-w-[2.25rem]',
};

export default function VitalsFieldRow({
  config,
  isEditing = false,
  values = {},
}: Props) {
  const {
    control,
  } = useFormContext<VitalsFormValues>();

  const displayValues = config.fields.reduce<Partial<Record<VitalFieldKey, string>>>((result, { key }) => {
    const value = values[key];

    if (value === DEFAULT_EMPTY_VALUE || isNil(value)) {
      return result;
    }

    return { ...result, [key]: value };
  }, {});

  const isCompositeField = config.fields.length > 1;
  const valueColumnWidth = isCompositeField ? '8.75rem' : '6.25rem';

  const renderField = ({ key, mask }: VitalEditorFieldConfig['fields'][number]) => {
    const sharedClassName = clsx(
      '!w-auto align-middle',
      isCompositeField
        ? '[&_.MuiInputBase-root]:!w-auto [&_input]:!w-auto [&_input]:!min-w-[3ch] [&_input]:[field-sizing:content]'
        : inputWidthByKey[key],
      isEditing
        ? {
          '[&_input]:text-right [&_input]:text-xs [&_input]:font-semibold [&_input]:text-gray-900': true,
          '[&_.MuiInputBase-root]:before:!border-b-transparent [&_.MuiInputBase-root]:after:!border-b-transparent [&_.MuiInputBase-root:hover:not(.Mui-disabled,.Mui-error):before]:!border-b-transparent': isCompositeField,
          '[&_.MuiInputBase-root]:before:!border-b-gray-300 [&_.MuiInputBase-root]:after:!border-b-blue-500': !isCompositeField,
        }
        : {
          '[&_input]:text-right': true,
          'pointer-events-none [&_input]:text-xs [&_input]:font-semibold [&_input]:text-gray-900 [&_input::placeholder]:text-xs [&_input::placeholder]:font-medium [&_input::placeholder]:text-gray-500 [&_.MuiInputBase-root]:before:!border-b-transparent [&_.MuiInputBase-root]:after:!border-b-transparent [&_.MuiInputBase-root:hover:not(.Mui-disabled,.Mui-error):before]:!border-b-transparent': true,
        },
    );

    return (
      <FormController
        name={key}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <MaskedTextField
            key={isEditing ? 'edit' : 'view'}
            {...field}
            mask={mask}
            variant="standard"
            className={sharedClassName}
            error={isEditing && !isNil(error)}
            showErrorText={false}
            size="small"
            placeholder={isEditing ? undefined : DEFAULT_EMPTY_VALUE}
            value={isEditing ? field.value ?? '' : displayValues[key]}
            onAccept={(value) => {
              if (isEditing) field.onChange(value);
            }}
            inputProps={isEditing ? undefined : {
              readOnly: true,
              tabIndex: -1,
            }}
          />
        )}
      />
    );
  };

  return (
    <li className="list-item leading-6">
      <div className="grid items-baseline gap-2" style={{ gridTemplateColumns: `minmax(7.5rem, 1fr) ${valueColumnWidth} 4.5rem` }}>
        <span className="font-medium text-gray-600">
          {config.viewLabel}
          :
          {' '}
        </span>
        <span className="inline-flex min-w-0 items-baseline justify-end gap-1.5 align-middle">
          <span className={clsx(
            isCompositeField
              ? 'inline-grid grid-cols-[auto_auto_auto] items-baseline gap-0 align-middle'
              : 'inline-flex items-center justify-end gap-1 align-middle',
            { 'border-b border-b-gray-300': isCompositeField && isEditing },
          )}
          >
            {config.fields.map((field, index) => (
              <Fragment key={field.key}>
                {renderField(field)}
                {index < config.fields.length - 1 && (
                  <span className={clsx('px-0.5 font-semibold text-gray-500', {
                    'text-xs': isCompositeField,
                    'text-sm': !isCompositeField,
                  })}>
                    {config.separator ?? '/'}
                  </span>
                )}
              </Fragment>
            ))}
          </span>
        </span>
        <span className="inline-flex justify-start text-gray-600 whitespace-nowrap">
          {config.unit}
        </span>
      </div>
    </li>
  );
}
